import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  makeStyles,
  Box,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DeveloperModeIcon from '@material-ui/icons/DeveloperMode';
import DnsIcon from '@material-ui/icons/Dns';
import { useNavigate } from 'react-router-dom';
import { useSystemInfo, SystemInfoResponse } from '../../buildInfo';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: theme.palette.type === 'dark' ? '#0f172a' : '#f8fafc',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  card: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#1e293b' : '#f1f5f9',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2.5),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.25),
  },
  valueBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    wordBreak: 'break-all',
  },
  value: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#10b981',
    display: 'inline-block',
    marginRight: theme.spacing(0.75),
    boxShadow: '0 0 8px #10b981',
  },
  chipTag: {
    fontFamily: 'monospace',
    fontWeight: 600,
    backgroundColor: theme.palette.type === 'dark' ? '#0369a1' : '#e0f2fe',
    color: theme.palette.type === 'dark' ? '#e0f2fe' : '#0369a1',
  },
}));

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label}`} arrow>
      <IconButton
        size="small"
        onClick={handleCopy}
        aria-label={`Copy ${label}`}
      >
        {copied ? (
          <CheckIcon fontSize="small" style={{ color: '#10b981' }} />
        ) : (
          <FileCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
}

function formatUptime(seconds: number): string {
  if (!seconds) return 'Just started';
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
}

export interface BuildInfoModalProps {
  open: boolean;
  onClose: () => void;
  systemInfo?: SystemInfoResponse | null;
}

export const BuildInfoModal: React.FC<BuildInfoModalProps> = ({
  open,
  onClose,
  systemInfo: propData,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { data: fetchedData } = useSystemInfo();
  const data = propData || fetchedData;

  const build = data?.build;
  const k8s = data?.kubernetes;
  const runtime = data?.runtime;

  const commitUrl =
    build?.projectUrl && build?.commitSha && build.commitSha !== 'development'
      ? `${build.projectUrl}/-/commit/${build.commitSha}`
      : null;

  const pipelineUrl =
    build?.pipelineUrl ||
    (build?.projectUrl && build?.pipelineId
      ? `${build.projectUrl}/-/pipelines/${build.pipelineId}`
      : null);

  const handleOpenDiagnostics = () => {
    onClose();
    navigate('/diagnostics');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <div className={classes.titleContent}>
          <DeveloperModeIcon color="primary" />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Instance & Build Information
          </Typography>
        </div>
        {build?.imageTag && (
          <Chip
            label={build.imageTag}
            size="small"
            className={classes.chipTag}
          />
        )}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Build Section */}
        <Typography className={classes.sectionTitle}>
          <DeveloperModeIcon fontSize="small" /> GitLab Build & Release
        </Typography>
        <div className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Git Commit</Typography>
              <div className={classes.valueBox}>
                <Typography className={classes.value}>
                  {build?.commitShortSha || 'local-dev'}
                </Typography>
                {build?.commitSha && (
                  <CopyButton text={build.commitSha} label="commit SHA" />
                )}
                {commitUrl && (
                  <Tooltip title="View commit on GitLab" arrow>
                    <IconButton
                      size="small"
                      component="a"
                      href={commitUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Git Branch</Typography>
              <Typography className={classes.value}>
                {build?.branch || 'main'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>GitLab Pipeline</Typography>
              <div className={classes.valueBox}>
                <Typography className={classes.value}>
                  {build?.pipelineIid
                    ? `#${build.pipelineIid}`
                    : build?.pipelineId
                    ? `#${build.pipelineId}`
                    : 'Manual Build'}
                </Typography>
                {pipelineUrl && (
                  <Tooltip title="View pipeline on GitLab" arrow>
                    <IconButton
                      size="small"
                      component="a"
                      href={pipelineUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Built At</Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {build?.buildTime
                  ? new Date(build.buildTime).toLocaleString()
                  : 'Just now'}
              </Typography>
            </Grid>
          </Grid>
        </div>

        {/* Kubernetes & Runtime Section */}
        <Typography className={classes.sectionTitle}>
          <DnsIcon fontSize="small" /> Kubernetes Runtime Environment
        </Typography>
        <div className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Cluster & Node</Typography>
              <Box display="flex" alignItems="center">
                <span className={classes.statusDot} />
                <Typography className={classes.value}>
                  {k8s?.nodeName || 'local-host'} (
                  {k8s?.clusterName || 'indectron'})
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Pod / Namespace</Typography>
              <div className={classes.valueBox}>
                <Typography
                  className={classes.value}
                  style={{ fontSize: '0.8rem' }}
                >
                  {k8s?.podName || 'local-dev'}
                </Typography>
                {k8s?.podName && (
                  <CopyButton text={k8s.podName} label="pod name" />
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Process Uptime</Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {runtime?.uptimeSeconds !== undefined
                  ? formatUptime(runtime.uptimeSeconds)
                  : 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Node.js Runtime</Typography>
              <Typography className={classes.value}>
                {runtime?.nodeVersion || process.version}
              </Typography>
            </Grid>
          </Grid>
        </div>
      </DialogContent>

      <Divider />

      <DialogActions
        style={{ padding: '12px 24px', justifyContent: 'space-between' }}
      >
        <Button
          variant="outlined"
          color="primary"
          startIcon={<DeveloperModeIcon />}
          onClick={handleOpenDiagnostics}
        >
          Full Diagnostics Dashboard
        </Button>
        <Button onClick={onClose} color="default" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
